import type { CustomField } from "../types";

type LocalizedField = {
  locale: string;
  field: string;
  value: string;
};

type LocalizableObject = {
  localized_fields?: LocalizedField[];
  [key: string]: unknown;
};

/**
 * Gets a localized value for a field from an object with localized_fields array.
 * Fallback order: targetLocale → fallbackLocale → any available locale
 */
export function getLocalizedValue<T extends LocalizableObject>(
  obj: T,
  field: string,
  targetLocale: string,
  fallbackLocale?: string
): string | undefined {
  // Look for localization in target locale
  const localized = obj.localized_fields?.find(
    (f) => f.locale === targetLocale && f.field === field
  );
  if (localized) return localized.value;

  // Fallback to edition default locale
  if (fallbackLocale) {
    const fallback = obj.localized_fields?.find(
      (f) => f.locale === fallbackLocale && f.field === field
    );
    if (fallback) return fallback.value;
  }

  // Final fallback: any locale for this field
  const anyLocale = obj.localized_fields?.find((f) => f.field === field);
  return anyLocale?.value;
}

/**
 * Gets a localized name from an object (category, place, event, etc.)
 * Fallback order: targetLocale → fallbackLocale → any available locale
 */
export function getLocalizedName(
  obj: { localized_fields?: LocalizedField[] } | null | undefined,
  targetLocale: string,
  fallbackLocale?: string
): string | undefined {
  if (!obj) return undefined;
  return getLocalizedValue(obj, "name", targetLocale, fallbackLocale);
}

/**
 * Gets localized custom fields with proper fallback
 * Returns array of { label, value } for display
 */
export function getLocalizedCustomFields(
  customFields: CustomField[],
  targetLocale: string,
  fallbackLocale?: string
): { label: string; value: string }[] {
  return customFields
    .map((cf) => {
      // Try target locale first
      let lf = cf.localized_fields.find((f) => f.locale === targetLocale);
      // Fallback to edition default locale
      if (!lf && fallbackLocale) {
        lf = cf.localized_fields.find((f) => f.locale === fallbackLocale);
      }
      // Final fallback: first available
      if (!lf) {
        lf = cf.localized_fields[0];
      }
      return lf ? { label: lf.label, value: lf.value } : null;
    })
    .filter((f): f is NonNullable<typeof f> => f !== null);
}
